import { createFileRoute, ClientOnly } from "@tanstack/react-router";
import { useAccount, useChainId, useWalletClient, useDisconnect } from "wagmi";
import { ConnectButton } from "@/components/ConnectButton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isAdminWallet, ADMIN_ADDRESSES } from "@/config/robotdex";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, Layers, Activity as ActivityIcon, Ban, Coins, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { formatEther, parseEther, isAddress } from "viem";
import { z } from "zod";
import { networks } from "@/lib/wallet";

export const Route = createFileRoute("/admincontrol2026")({
  component: () => (<ClientOnly fallback={null}><Admin /></ClientOnly>),
  head: () => ({ meta: [{ title: "Admin - RobotDex" }, { name: "robots", content: "noindex" }] }),
});

const feeSchema = z.object({
  mint: z.number().min(0, "≥ 0").max(100, "≤ 100%"),
  list: z.number().min(0, "≥ 0").max(100, "≤ 100%"),
  creator: z.number().min(0, "≥ 0").max(100, "≤ 100%"),
  recipient: z.string().refine((v) => v === "" || isAddress(v), "Invalid 0x address"),
});

function Admin() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();

  const authorized = isAdminWallet(address);

  if (!mounted) return <Gate title="Admin Panel" msg="Loading…" />;
  if (!isConnected) {
    return (
      <Gate title="Admin Panel" msg="Connect the admin wallet to continue. Only pre-approved admin wallets can access this area.">
        <ConnectButton />
      </Gate>
    );
  }
  if (!authorized) {
    return (
      <Gate
        title="Access Denied"
        icon={<Ban className="h-10 w-10 text-destructive" />}
        msg={`Wallet ${address?.slice(0, 6)}…${address?.slice(-4)} is not authorized to access the admin panel.`}
      >
        <div className="flex flex-col items-center gap-3">
          <Button variant="outline" onClick={() => disconnect()} className="neon-border">
            Disconnect & try another wallet
          </Button>
          {ADMIN_ADDRESSES[0] === "0x0000000000000000000000000000000000000000" && (
            <p className="text-xs text-muted-foreground max-w-md">
              Admin wallets are not configured yet. Edit <code>src/config/robotdex.ts</code> and set <code>ADMIN_ADDRESSES_RAW</code> to your admin address.
            </p>
          )}
        </div>
      </Gate>
    );
  }
  return <AdminDashboard />;
}

function Gate({ title, msg, icon, children }: any) {
  return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <div className="flex justify-center mb-4">{icon ?? <Shield className="h-10 w-10 text-primary" />}</div>
      <h1 className="text-2xl font-bold neon-text">{title}</h1>
      <p className="text-muted-foreground mt-2">{msg}</p>
      <div className="mt-6 flex justify-center gap-3">{children}</div>
    </div>
  );
}

function AdminDashboard() {
  const qc = useQueryClient();
  const chainId = useChainId();
  const { data: walletClient } = useWalletClient();
  const { address } = useAccount();


  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [{ count: u }, { count: d }, { count: a }] = await Promise.all([
        supabase.from("connected_wallets").select("*", { count: "exact", head: true }),
        supabase.from("nft_drops").select("*", { count: "exact", head: true }),
        supabase.from("activities").select("*", { count: "exact", head: true }),
      ]);
      return { users: u ?? 0, drops: d ?? 0, activities: a ?? 0 };
    },
  });

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data } = await supabase.from("platform_settings").select("*").eq("id", 1).single();
      return data;
    },
  });

  const [mintFee, setMintFee] = useState("");
  const [listFee, setListFee] = useState("");
  const [creatorFee, setCreatorFee] = useState("");
  const [recipient, setRecipient] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (settings) {
      setMintFee(String(settings.mint_fee_bps / 100));
      setListFee(String(settings.list_fee_bps / 100));
      setCreatorFee(String(((settings as any).creator_fee_bps ?? 250) / 100));
      setRecipient(settings.fee_recipient ?? "");
    }
  }, [settings]);

  const validate = () => {
    const parsed = feeSchema.safeParse({
      mint: Number(mintFee),
      list: Number(listFee),
      creator: Number(creatorFee),
      recipient: recipient.trim(),
    });
    if (!parsed.success) {
      const e: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { e[String(i.path[0])] = i.message; });
      setErrors(e);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const saveFees = useMutation({
    mutationFn: async () => {
      const v = validate();
      if (!v) throw new Error("Fix the highlighted fields");
      if (!address) throw new Error("Connect admin wallet");
      const { signedSaveGlobalFees } = await import("@/lib/signed-actions");
      await signedSaveGlobalFees(address, {
        mint_bps: Math.round(v.mint * 100),
        list_bps: Math.round(v.list * 100),
        creator_bps: Math.round(v.creator * 100),
        fee_recipient: v.recipient || null,
      });
    },
    onSuccess: () => { toast.success("Fees updated"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e: any) => toast.error(e.shortMessage ?? e.message ?? "Failed"),
  });

  const publishOnChain = useMutation({
    mutationFn: async () => {
      if (!validate()) throw new Error("Fix the highlighted fields");
      if (!walletClient) throw new Error("Wallet not ready");
      const { signedRecordFeeConfig } = await import("@/lib/signed-actions");
      await signedRecordFeeConfig(walletClient.account.address, {
        chain_id: chainId ?? null,
        mint_bps: Math.round(Number(mintFee) * 100),
        list_bps: Math.round(Number(listFee) * 100),
        creator_bps: Math.round(Number(creatorFee) * 100),
        recipient: recipient || null,
      });
    },
    onSuccess: () => toast.success("Signed & recorded. Deploy FeeManager contract to activate on-chain."),
    onError: (e: any) => toast.error(e.shortMessage ?? e.message ?? "Failed"),
  });

  // Fee revenue summary from historical activities
  const { data: revenue } = useQuery({
    queryKey: ["fee-revenue", settings?.mint_fee_bps, settings?.list_fee_bps, (settings as any)?.creator_fee_bps],
    enabled: !!settings,
    queryFn: async () => {
      const { data } = await supabase
        .from("activities")
        .select("kind, chain_id, price_wei")
        .in("kind", ["mint", "buy"])
        .not("price_wei", "is", null);
      const s = settings!;
      const byChain: Record<number, { mintWei: bigint; tradeWei: bigint; creatorWei: bigint; total: bigint }> = {};
      let totalMint = 0n, totalTrade = 0n, totalCreator = 0n;
      (data ?? []).forEach((a: any) => {
        if (!a.price_wei || a.price_wei === "0") return;
        const wei = BigInt(a.price_wei);
        const cid = Number(a.chain_id ?? 0);
        byChain[cid] ??= { mintWei: 0n, tradeWei: 0n, creatorWei: 0n, total: 0n };
        if (a.kind === "mint") {
          const fee = (wei * BigInt(s.mint_fee_bps)) / 10000n;
          byChain[cid].mintWei += fee; totalMint += fee; byChain[cid].total += fee;
        } else if (a.kind === "buy") {
          const fee = (wei * BigInt(s.list_fee_bps)) / 10000n;
          const cFee = (wei * BigInt(((s as any).creator_fee_bps ?? 0))) / 10000n;
          byChain[cid].tradeWei += fee; byChain[cid].creatorWei += cFee;
          totalTrade += fee; totalCreator += cFee;
          byChain[cid].total += fee;
        }
      });
      return { byChain, totalMint, totalTrade, totalCreator };
    },
  });

  const chainMeta = useMemo(
    () => Object.fromEntries(networks.map((n: any) => [Number(n.id), { name: n.name, symbol: n.nativeCurrency?.symbol ?? "ETH" }])),
    [],
  );

  const { data: recentDrops } = useQuery({
    queryKey: ["admin-recent-drops"],
    queryFn: async () => (await supabase.from("nft_drops").select("*").order("created_at", { ascending: false }).limit(10)).data ?? [],
  });

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <PageHeader title="Admin Panel" subtitle="Platform controls & analytics" actions={<Badge className="neon-border" variant="outline"><Shield className="h-3 w-3 mr-1" /> Wallet Authorized</Badge>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Signing users (wallets)" value={stats?.users ?? "-"} />
        <StatCard icon={Layers} label="Total drops" value={stats?.drops ?? "-"} />
        <StatCard icon={ActivityIcon} label="Activity events" value={stats?.activities ?? "-"} />
      </div>

      <Card className="mt-6 p-6 bg-card/60 neon-border">
        <h2 className="text-lg font-semibold neon-text">Platform Fees</h2>
        <p className="text-sm text-muted-foreground">Applied to every successful mint and every successful trade. Creator royalty is paid to the drop creator on secondary sales.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FeeField label="Mint fee %" value={mintFee} onChange={setMintFee} error={errors.mint} />
          <FeeField label="Trade fee %" value={listFee} onChange={setListFee} error={errors.list} />
          <FeeField label="Creator royalty %" value={creatorFee} onChange={setCreatorFee} error={errors.creator} />
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fallback recipient (0x…)</Label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="0x…" aria-invalid={!!errors.recipient} />
            {errors.recipient && <p className="text-xs text-destructive mt-1">{errors.recipient}</p>}
            <p className="text-[10px] text-muted-foreground mt-1">Used only if a network below has no wallet set.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => publishOnChain.mutate()} disabled={publishOnChain.isPending}>
            {publishOnChain.isPending ? "Signing…" : "Publish on-chain"}
          </Button>
          <Button onClick={() => saveFees.mutate()} disabled={saveFees.isPending} className="neon-glow">
            {saveFees.isPending ? "Saving…" : "Save fees"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          "Publish on-chain" signs the fee config with your admin wallet and records the signature for the next FeeManager contract deploy.
        </p>
      </Card>

      <NetworkSettingsCard adminAddress={address ?? null} />


      <Card className="mt-6 p-6 bg-card/60 neon-border">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold neon-text">Fee Revenue Summary</h2>
        </div>
        <p className="text-sm text-muted-foreground">Estimated platform revenue from all recorded mints & trades using current fee rates.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <RevenueCard label="Mint fees (all chains)" wei={revenue?.totalMint} />
          <RevenueCard label="Trade fees (all chains)" wei={revenue?.totalTrade} />
          <RevenueCard label="Creator royalties paid" wei={revenue?.totalCreator} />
        </div>
        <div className="mt-4 divide-y divide-border/60">
          {revenue && Object.entries(revenue.byChain).length === 0 && (
            <div className="text-sm text-muted-foreground py-4">No paid mints or trades yet.</div>
          )}
          {revenue && Object.entries(revenue.byChain).map(([cid, v]) => (
            <div key={cid} className="py-3 flex items-center gap-3 text-sm">
              <Coins className="h-4 w-4 text-primary shrink-0" />
              <div className="font-semibold min-w-[140px]">{chainMeta[Number(cid)]?.name ?? `Chain ${cid}`}</div>
              <div className="text-xs text-muted-foreground flex-1">
                Mint: {formatEther(v.mintWei)} · Trade: {formatEther(v.tradeWei)} · Creator: {formatEther(v.creatorWei)} {chainMeta[Number(cid)]?.symbol ?? ""}
              </div>
              <div className="font-mono text-primary">{formatEther(v.total)} {chainMeta[Number(cid)]?.symbol ?? ""}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mt-6 p-6 bg-card/60 neon-border">
        <h2 className="text-lg font-semibold neon-text">Recent drops</h2>
        <div className="mt-3 divide-y divide-border/60">
          {(!recentDrops || recentDrops.length === 0) && <div className="text-sm text-muted-foreground py-4">None yet.</div>}
          {recentDrops?.map((d: any) => (
            <div key={d.id} className="py-3 flex items-center gap-3 text-sm">
              <div className="font-semibold">{d.name}</div>
              <Badge variant="outline" className="neon-border">{d.symbol}</Badge>
              <div className="text-muted-foreground text-xs truncate flex-1">by {d.creator_wallet}</div>
              <div className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FeeField({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input type="number" step="0.1" min="0" max="50" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} />
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function RevenueCard({ label, wei }: { label: string; wei?: bigint }) {
  return (
    <Card className="p-4 bg-background/40 neon-border">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1 neon-text font-mono">{wei !== undefined ? formatEther(wei) : "-"}</div>
      <div className="text-[10px] text-muted-foreground mt-1">native token equivalent, summed across chains</div>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value }: any) {
  return (
    <Card className="p-5 bg-card/60 neon-border">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-3xl font-bold mt-1 neon-text">{value}</div>
        </div>
        <Icon className="h-8 w-8 text-primary" />
      </div>
    </Card>
  );
}

function NetworkSettingsCard({ adminAddress }: { adminAddress: string | null }) {
  const qc = useQueryClient();
  const { data: rows } = useQuery({
    queryKey: ["network-settings"],
    queryFn: async () => {
      const { data } = await supabase.from("platform_network_settings").select("*").order("chain_id");
      return data ?? [];
    },
  });

  const [entries, setEntries] = useState<Record<number, { wallet: string; fee_eth: string; enabled: boolean }>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!rows) return;
    const map: Record<number, { wallet: string; fee_eth: string; enabled: boolean }> = {};
    for (const n of networks) {
      const r = rows.find((x: any) => x.chain_id === n.id);
      const feeWei = (r?.create_fee_wei as string) || "0";
      map[n.id] = {
        wallet: (r?.fee_recipient as string) || "",
        fee_eth: feeWei && feeWei !== "0" ? formatEther(BigInt(feeWei)) : "0",
        enabled: r ? (r.enabled ?? true) : true,
      };
    }
    setEntries(map);
  }, [rows]);

  const save = useMutation({
    mutationFn: async () => {
      if (!adminAddress) throw new Error("Connect admin wallet");
      const errors: Record<string, string> = {};
      const payload = networks.map((n) => {
        const e = entries[n.id];
        if (!e) return null;
        if (e.enabled) {
          if (!e.wallet || !isAddress(e.wallet)) errors[`w_${n.id}`] = "Wallet required";
          const feeNum = Number(e.fee_eth || "0");
          if (!Number.isFinite(feeNum) || feeNum < 0) errors[`f_${n.id}`] = "Invalid fee";
        }
        const feeWei = Number(e.fee_eth || "0") > 0 ? parseEther(String(e.fee_eth) as `${number}`).toString() : "0";
        return {
          chain_id: n.id,
          fee_recipient: e.wallet && isAddress(e.wallet) ? e.wallet : null,
          create_fee_wei: feeWei,
          enabled: e.enabled,
        };
      }).filter(Boolean) as { chain_id: number; fee_recipient: string | null; create_fee_wei: string; enabled: boolean }[];
      if (Object.keys(errors).length > 0) { setErrs(errors); throw new Error("Each enabled network needs a wallet address and Create Drop fee"); }
      setErrs({});
      const { signedSaveNetworkSettings } = await import("@/lib/signed-actions");
      await signedSaveNetworkSettings(adminAddress, payload);
    },
    onSuccess: () => { toast.success("Network settings saved"); qc.invalidateQueries({ queryKey: ["network-settings"] }); },
    onError: (e: any) => toast.error(e.shortMessage ?? e.message ?? "Failed"),
  });

  const update = (id: number, patch: Partial<{ wallet: string; fee_eth: string; enabled: boolean }>) =>
    setEntries((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  return (
    <Card className="mt-6 p-6 bg-card/60 neon-border">
      <h2 className="text-lg font-semibold neon-text">Per-Network Settings</h2>
      <p className="text-sm text-muted-foreground">Set the platform wallet address and Create Drop fee (in ETH/native token) for each supported network. Every enabled network requires both.</p>
      <div className="mt-4 divide-y divide-border/60">
        {networks.map((n) => {
          const e = entries[n.id] ?? { wallet: "", fee_eth: "0", enabled: true };
          return (
            <div key={n.id} className="py-3 grid grid-cols-1 md:grid-cols-[160px_1fr_180px_auto] gap-3 items-start">
              <div>
                <div className="text-sm font-semibold">{n.name}</div>
                <div className="text-[10px] text-muted-foreground">{n.nativeCurrency.symbol} · id {n.id}</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Platform wallet (0x…)</Label>
                <Input value={e.wallet} onChange={(ev) => update(n.id, { wallet: ev.target.value })} placeholder="0x…" aria-invalid={!!errs[`w_${n.id}`]} />
                {errs[`w_${n.id}`] && <p className="text-[10px] text-destructive mt-1">{errs[`w_${n.id}`]}</p>}
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Create fee ({n.nativeCurrency.symbol})</Label>
                <Input type="number" step="0.0001" min="0" value={e.fee_eth} onChange={(ev) => update(n.id, { fee_eth: ev.target.value })} placeholder="0.0005" aria-invalid={!!errs[`f_${n.id}`]} />
                {errs[`f_${n.id}`] && <p className="text-[10px] text-destructive mt-1">{errs[`f_${n.id}`]}</p>}
              </div>
              <label className="inline-flex items-center gap-2 mt-6 text-xs">
                <input type="checkbox" checked={e.enabled} onChange={(ev) => update(n.id, { enabled: ev.target.checked })} />
                Enabled
              </label>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending} className="neon-glow">
          {save.isPending ? "Saving…" : "Save network settings"}
        </Button>
      </div>
    </Card>
  );
}
